import { useState } from 'react';
import { Sparkles, Coffee, Briefcase, Smile, Moon, Feather, Zap, Loader2, Check, Copy, ArrowRight, RefreshCw, Settings2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const POLISH_STYLES = [
  { id: 'casual', name: 'Casual', icon: Coffee, description: 'Conversational and relaxed' },
  { id: 'pro', name: 'Professional', icon: Briefcase, description: 'Polished and composed' },
  { id: 'humor', name: 'Add Humor', icon: Smile, description: 'Light wit where it fits' },
  { id: 'darker', name: 'Darker', icon: Moon, description: 'More dramatic weight' },
  { id: 'poetic', name: 'Poetic', icon: Feather, description: 'More lyrical texture' },
  { id: 'punchy', name: 'Punchy', icon: Zap, description: 'Shorter and sharper' },
];

const DEPTH_LEVELS = [
  { id: 'light', name: 'Touch Up', description: 'Correct and smooth' },
  { id: 'medium', name: 'Enhance', description: 'Improve flow and clarity' },
  { id: 'deep', name: 'Flesh It Out', description: 'Expand thin moments' },
];

function PolishStyles({ content, onPolish, polishedContent, isPolishing, onUsePolished, onAcceptAndNext, hasNextChapter, polishState = 'none' }) {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedDepth, setSelectedDepth] = useState('medium');
  const [lastUsedStyle, setLastUsedStyle] = useState('pro');
  const [showDepthOptions, setShowDepthOptions] = useState(false);

  const canPolish = content && content.trim().length > 50;

  const handlePolish = (styleId) => {
    if (!canPolish || isPolishing) return;
    setSelectedStyle(styleId);
    setLastUsedStyle(styleId);
    onPolish(styleId, selectedDepth);
  };

  const handleRePolish = () => {
    handlePolish(lastUsedStyle);
  };

  const handleUsePolished = () => {
    if (onUsePolished && polishedContent) {
      onUsePolished(polishedContent);
      toast.success('Polished version applied to your draft.');
    }
  };

  const handleAcceptAndNext = () => {
    if (onAcceptAndNext && polishedContent) {
      onAcceptAndNext(polishedContent);
    }
  };

  const handleCopyPolished = () => {
    if (polishedContent) {
      navigator.clipboard.writeText(polishedContent);
      toast.success('Copied to clipboard.');
    }
  };
  const isOutdated = polishState === 'outdated';
  const styleButtonBase = 'group flex min-h-[58px] items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition';

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E7DED1]/80 bg-[#FFFCF7]/82 text-[#2A2420] shadow-[0_18px_42px_-36px_rgba(58,43,31,0.55)] backdrop-blur-sm dark:border-[#302820]/80 dark:bg-[#211E1A]/82 dark:text-[#F0EBE4]" data-testid="polish-styles">
      <div className="border-b border-[#ECE3D7]/80 px-4 py-4 dark:border-[#302820]/70">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D8CBBC] bg-[#F7F0E8] text-[#5C4D6B] dark:border-[#3A332C] dark:bg-[#29241F] dark:text-[#C9BBD8]">
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <h3 className="text-sm font-semibold">
                  {isOutdated ? 'Refresh polish' : 'Polish'}
                </h3>
                <p className="mt-0.5 text-xs leading-relaxed text-[#7C7065] dark:text-[#A99F95]">
                  Refine the chapter while keeping your voice.
                </p>
              </div>
            </div>
          </div>
          {isOutdated && (
            <span className="rounded-full border border-[#D8B36B]/40 bg-[#F7EBD4] px-2.5 py-1 text-[11px] font-medium text-[#7C5622] dark:border-[#7A5A28]/60 dark:bg-[#342A1A] dark:text-[#E7C68D]">
              Outdated
            </span>
          )}
        </div>
      </div>

      {isOutdated && (
        <div className="border-b border-[#E8D8BD]/70 bg-[#FBF4E8] p-3 dark:border-[#463724]/70 dark:bg-[#282116]">
          <Button
            onClick={handleRePolish}
            disabled={!canPolish || isPolishing}
            className="min-h-[44px] w-full rounded-full bg-[#5C4D6B] font-medium text-white hover:bg-[#4A3D58] dark:bg-[#8A789D] dark:hover:bg-[#796889]"
            data-testid="repolish-button"
          >
            {isPolishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh polish
              </>
            )}
          </Button>
        </div>
      )}

      <div className="border-b border-[#ECE3D7]/80 px-4 py-3 dark:border-[#302820]/70">
        <button
          onClick={() => setShowDepthOptions(!showDepthOptions)}
          className="flex w-full items-center justify-between rounded-xl px-1 py-1 text-left text-xs text-[#6F655C] transition hover:text-[#2A2420] dark:text-[#A99F95] dark:hover:text-[#F0EBE4]"
          data-testid="depth-toggle"
        >
          <span className="flex items-center gap-2">
            <Settings2 className="h-3.5 w-3.5" />
            <span>Depth</span>
            <span className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">
              {DEPTH_LEVELS.find(d => d.id === selectedDepth)?.name}
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showDepthOptions ? 'rotate-180' : ''}`} />
        </button>

        {showDepthOptions && (
          <div className="mt-3 grid gap-2">
            {DEPTH_LEVELS.map((depth) => (
              <button
                key={depth.id}
                onClick={() => {
                  setSelectedDepth(depth.id);
                  setShowDepthOptions(false);
                }}
                data-testid={`depth-${depth.id}`}
                className={`rounded-xl border px-3 py-2 text-left transition ${
                  selectedDepth === depth.id
                    ? 'border-[#BCA5CB] bg-[#F2EAF7] text-[#4D3F5B] dark:border-[#6B5578] dark:bg-[#2C2332] dark:text-[#D7C8E6]'
                    : 'border-[#ECE3D7] bg-white/58 text-[#6F655C] hover:border-[#D8CBBC] hover:bg-[#FBF8F2] dark:border-[#302820] dark:bg-[#1C1A18]/70 dark:text-[#B8AEA4] dark:hover:border-[#4A4036]'
                }`}
              >
                <div className="text-xs font-medium">{depth.name}</div>
                <div className="mt-0.5 text-[11px] opacity-75">{depth.description}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 p-3">
        {POLISH_STYLES.map((style) => {
          const Icon = style.icon;
          const isSelected = selectedStyle === style.id;
          const isCurrentlyPolishing = isPolishing && selectedStyle === style.id;

          return (
            <button
              key={style.id}
              onClick={() => handlePolish(style.id)}
              aria-disabled={!canPolish || isPolishing}
              data-testid={`polish-style-${style.id}`}
              title={`${style.description} - Click to refine your text in this style`}
              className={`${styleButtonBase} ${
                !canPolish
                  ? 'cursor-not-allowed border-[#D8CBBC] bg-[#F4EDE4] dark:border-[#5A4E43] dark:bg-[#2B2621]'
                  : isPolishing && !isCurrentlyPolishing
                    ? 'cursor-wait border-[#D8CBBC] bg-[#F6EFE7] dark:border-[#5A4E43] dark:bg-[#2B2621]'
                  : isSelected
                    ? 'border-[#BCA5CB] bg-[#F4EEF8] dark:border-[#6B5578] dark:bg-[#2B2430]'
                    : 'border-[#E3D8CA] bg-[#F7F1E9]/90 hover:border-[#CDBED8] hover:bg-[#FFFCF7] dark:border-[#4A4036] dark:bg-[#241F1B] dark:hover:border-[#6B5578] dark:hover:bg-[#2B2430]'
              }`}
            >
              <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${
                !canPolish || (isPolishing && !isCurrentlyPolishing)
                  ? 'border-[#BDAF9F] bg-[#FFF9F1] text-[#4A4038] dark:border-[#6A5D52] dark:bg-[#171411] dark:text-[#F4EADF]'
                  : isSelected
                  ? 'border-[#BCA5CB] bg-white text-[#5C4D6B] dark:border-[#6B5578] dark:bg-[#211E1A] dark:text-[#D7C8E6]'
                  : 'border-[#D8CBBC] bg-[#FFFCF7] text-[#5F554B] dark:border-[#5A4E43] dark:bg-[#171411] dark:text-[#E6DCD1]'
              }`}>
                {isCurrentlyPolishing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-medium ${
                  !canPolish || (isPolishing && !isCurrentlyPolishing)
                    ? 'text-[#2A2420] dark:text-[#FFF8EF]'
                    : 'text-[#2A2420] dark:text-[#F0EBE4]'
                }`}>{style.name}</span>
                <span className={`mt-0.5 block truncate text-xs ${
                  !canPolish || (isPolishing && !isCurrentlyPolishing)
                    ? 'text-[#5F554B] dark:text-[#E3D7CA]'
                    : 'text-[#6F655C] dark:text-[#CFC3B7]'
                }`}>{style.description}</span>
              </span>
              {isSelected && !isPolishing && polishedContent && !isOutdated && (
                <Check className="h-4 w-4 flex-shrink-0 text-[#5C4D6B] dark:text-[#D7C8E6]" />
              )}
            </button>
          );
        })}
      </div>

      {polishedContent && !isOutdated && (
        <div className="border-t border-[#E7DED1]/80 bg-[#FBF8F2]/70 dark:border-[#302820]/70 dark:bg-[#1C1A18]/60">
          <div className="space-y-3 px-4 py-4">
            <div>
              <h4 className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">Polished version ready</h4>
              <p className="mt-1 text-xs text-[#7C7065] dark:text-[#A99F95]">Review it below before applying it.</p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleUsePolished}
                size="sm"
                className="min-h-[44px] flex-1 rounded-full bg-[#5C4D6B] text-white hover:bg-[#4A3D58] dark:bg-[#8A789D] dark:hover:bg-[#796889]"
                data-testid="use-polished-button"
              >
                <Check className="mr-1 h-4 w-4" />
                Use
              </Button>
              <Button
                onClick={handleCopyPolished}
                size="sm"
                variant="outline"
                className="min-h-[44px] rounded-full border-[#D8CBBC] bg-white/70 text-[#5C4D6B] hover:bg-[#F7F1E9] dark:border-[#3A332C] dark:bg-[#211E1A] dark:text-[#D7C8E6]"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            <Button
              onClick={handleAcceptAndNext}
              size="sm"
              className="min-h-[44px] w-full rounded-full bg-[#2F6F5E] font-medium text-white hover:bg-[#285F51]"
              data-testid="accept-and-next-button"
            >
              <Check className="mr-1 h-4 w-4" />
              {hasNextChapter ? (
                <>Accept & Next <ArrowRight className="ml-1 h-4 w-4" /></>
              ) : (
                <>Accept & Finish <ArrowRight className="ml-1 h-4 w-4" /></>
              )}
            </Button>
          </div>

          <div className="max-h-[240px] overflow-y-auto border-t border-[#ECE3D7]/80 bg-[#FFFCF7] p-4 dark:border-[#302820]/70 dark:bg-[#211E1A]">
            <p className="whitespace-pre-wrap text-sm leading-7 text-[#3D3530] dark:text-[#E8DFD4]">
              {polishedContent}
            </p>
          </div>
        </div>
      )}

      {canPolish && !polishedContent && !isPolishing && (
        <div className="border-t border-[#ECE3D7]/80 px-4 py-3 text-center text-xs text-[#7C7065] dark:border-[#302820]/70 dark:text-[#A99F95]">
          Polish will create a preview first. Your draft stays unchanged until you accept it.
        </div>
      )}

      {!canPolish && (
        <div className="border-t border-[#ECE3D7]/80 px-4 py-3 text-center text-xs text-[#7C7065] dark:border-[#302820]/70 dark:text-[#A99F95]">
          Write at least 50 words to enable polish.
        </div>
      )}
    </div>
  );
}

export default PolishStyles;
